import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  Appointment,
  CalendarEvent,
  Event,
  Staffer,
} from '@principle-theorem/principle-core';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import {
  IScheduleSummaryEventable,
  isAppointmentSummary,
  isCalendarEventSummary,
  type IEvent,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import { ReplaySubject, combineLatest, type Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-eventable-header',
  templateUrl: './eventable-header.component.html',
  styleUrls: ['./eventable-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EventableHeaderComponent {
  private _organisation = inject(OrganisationService);
  eventable$ = new ReplaySubject<IScheduleSummaryEventable>(1);
  staffer$ = new ReplaySubject<WithRef<IStaffer>>(1);
  event$: Observable<IEvent>;
  hasAlert$: Observable<boolean>;
  canEditEvent$: Observable<boolean>;
  @Output() edit = new EventEmitter<IScheduleSummaryEventable>();

  @Input()
  set eventable(eventable: IScheduleSummaryEventable) {
    this.eventable$.next(eventable);
  }

  @Input()
  set staffer(staffer: WithRef<IStaffer>) {
    if (staffer) {
      this.staffer$.next(staffer);
    }
  }

  constructor() {
    this.event$ = this.eventable$.pipe(
      map((eventable) => this._toEvent(eventable))
    );
    this.hasAlert$ = this.eventable$.pipe(
      map((eventable) => this._hasAlert(eventable))
    );
    this.initCanEdit();
  }

  initCanEdit(): void {
    const hasBypassLockedPermission$ = this.staffer$.pipe(
      switchMap((staffer) =>
        Staffer.hasPermission$(
          staffer,
          AppointmentPermissions.SchedulingBypassLocked
        )
      )
    );
    this.canEditEvent$ = combineLatest([
      this.staffer$,
      this.eventable$,
      hasBypassLockedPermission$,
      this._organisation.userPermissions$,
    ]).pipe(
      map(([staffer, event, hasBypassLockedPermission, userPermissions]) =>
        CalendarEvent.canEdit(
          event,
          staffer,
          hasBypassLockedPermission,
          userPermissions
        )
      ),
      startWith(false)
    );
  }

  editEvent(eventable: IScheduleSummaryEventable): void {
    this.edit.emit(eventable);
  }

  calendarEventTitle(event: IScheduleSummaryEventable): string | undefined {
    if (!isCalendarEventSummary(event)) {
      return;
    }
    return event.metadata.label;
  }

  private _hasAlert(eventable: IScheduleSummaryEventable): boolean {
    if (isAppointmentSummary(eventable)) {
      return Appointment.getSchedulingConflicts(eventable).length > 0;
    }
    return false;
  }

  private _toEvent(eventable: IScheduleSummaryEventable): IEvent {
    return Event.init(eventable.event);
  }
}
